import Button from '@ui/components/Button/Button';
import Card from '@ui/components/Card/Card';
import Content from '@ui/components/Content/Content';
import FontAwesomeProIcon from '@ui/components/FontAwesomeProIcon/FontAwesomeProIcon';
import Heading from '@ui/components/Heading/Heading';
import Image from '@ui/components/Image/Image';
import Link from '@ui/components/Link/Link';
import styles from '@ui/components/IconText/IconText.module.scss';

import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { VisuallyHidden } from '@ariakit/react';
import { getHeadingTag } from '../../utils';

const IconText = ({
  isCard = false,
  infoButton = null,
  glyph = null,
  iconTheme = 'default',
  headingLevel = 3,
  hasInfoIconButton = null,
  heading = null,
  text,
  limit = 0,
  linkProps = null,
  linkText = null,
  theme = 'default',
  className = null,
  hasBackground = false,
  hasBorder = false,
  variant = 'default',
}) => {
  const Tag = isCard ? Card : 'div';
  const ellipsis = '…';
  const currIconTheme = iconTheme || theme;
  const textSize = isCard || variant === 'inline' ? 'regular' : 'md';

  return (
    <Tag
      className={classNames(
        styles.container,
        styles[iconTheme],
        styles[variant],
        {
          [styles.card]: isCard,
          [styles.hasBackground]: hasBackground,
          [styles.hasBorder]: hasBorder,
        },
        className,
      )}
    >
      {!glyph?.src ? (
        <FontAwesomeProIcon
          className={classNames(
            styles[currIconTheme],
            styles.icon,
            styles[glyph],
            styles[variant],
            {
              [styles.cardIcon]: isCard,
            },
          )}
          icon={glyph}
          role="presentation"
        />
      ) : (
        <Image alt={glyph?.title} className={styles.icon} src={glyph?.src} />
      )}
      <div>
        {heading && (
          <Heading
            as={getHeadingTag(headingLevel)}
            className={classNames(styles.heading, {
              [styles.inlineHeading]: variant === 'inline',
            })}
            size="h5"
            theme={theme}
          >
            {heading}
            {hasInfoIconButton && (
              <Button
                className={classNames(
                  styles.informationButton,
                  styles[variant],
                )}
                onClick={infoButton}
                shape="bubble"
                type="button"
              >
                <FontAwesomeProIcon
                  className={classNames(styles.infoIcon, styles[variant])}
                  color="primary"
                  icon="info-circle"
                  role="presentation"
                />
              </Button>
            )}
          </Heading>
        )}
        {!!text && (
          <Content
            aria-hidden="true"
            as="p"
            noSpacing
            size={textSize}
            theme={theme}
            withParser
          >
            {!!limit && text?.length > limit
              ? `${text.substring(0, limit)}${ellipsis}`
              : text}
          </Content>
        )}
        {!!limit && <VisuallyHidden>{text}</VisuallyHidden>}
        {!!linkText && !!linkProps && (
          <Button
            render={
              <Link {...linkProps} className={styles.link}>
                {linkText}
              </Link>
            }
            theme="primary"
            variant="link"
          >
            {linkText}
          </Button>
        )}
      </div>
    </Tag>
  );
};

IconText.propTypes = {
  className: PropTypes.string,
  glyph: PropTypes.string,
  hasBackground: PropTypes.bool,
  hasBorder: PropTypes.bool,
  hasInfoIconButton: PropTypes.bool,
  heading: PropTypes.string,
  headingLevel: PropTypes.number,
  iconTheme: PropTypes.oneOf([
    'default',
    'contrast',
    'primary',
    'secondary',
    'primary',
    'gold',
    'red',
    'green',
  ]),
  infoButton: PropTypes.func,
  isCard: PropTypes.bool,
  limit: PropTypes.number,
  linkProps: PropTypes.shape({
    linkText: PropTypes.string,
    rel: PropTypes.string,
    target: PropTypes.oneOf(['_blank', '_parent', '_self', '_top']),
    to: PropTypes.string,
  }),
  linkText: PropTypes.string,
  text: PropTypes.string,
  theme: PropTypes.oneOf(['default', 'contrast']),
  variant: PropTypes.oneOf(['default', 'inline']),
};

export default IconText;
